import { Button, Container, FormControl, Grid, InputLabel, List, ListItem, ListItemText, MenuItem, Select, TextField } from '@material-ui/core';
import Alert from "@material-ui/lab/Alert";
import React, { ChangeEvent, ChangeEventHandler, DetailedHTMLProps, forwardRef, InputHTMLAttributes, LegacyRef, MutableRefObject, RefCallback, Suspense, useCallback, useRef, useState } from 'react';
import { Path, useForm, UseFormRegister, SubmitHandler, Validate } from "react-hook-form";
import { SignatureV4 } from '@aws-sdk/signature-v4';
import { Sha256 } from '@aws-crypto/sha256-js'
import { HttpRequest }from '@aws-sdk/protocol-http'

import './App.css';

export type InputFileProps = Omit<
  DetailedHTMLProps<
    InputHTMLAttributes<HTMLInputElement>,
    HTMLInputElement
  >,
  'type' | 'value' | 'defaultValue'
>

export const InputFile = forwardRef<HTMLInputElement, InputFileProps>(({ onChange, ...props }, ref) => {
    const inputRef = useRef<HTMLInputElement>(null)
    // 通常は同じfilesへの参照を保持し続けreactのライフサイクルで検知できないため、新たにFileListを生成する
    const onInputChange = useCallback<ChangeEventHandler<HTMLInputElement>>(
      (event) => {
        event.target.files = createFileList(event.target.files)
        onChange && onChange(event)
      },
      [onChange]
    )
    return (
      <input
        {...props}
        type="file"
        onChange={onInputChange}
        ref={mergeRefs([ref, inputRef])}
      />
    )
  }
)
// FileListは直接生成することができないため、DataTransferを経由する
export const createFileList: (fileList?: FileList | null) => FileList = (
  fileList
) => {
  const dataTransfer = new DataTransfer()

  if (!fileList) {
    return dataTransfer.files
  }

  for (const file of fileList) {
    dataTransfer.items.add(file)
  }

  return dataTransfer.files
}

/**
 * 複数のrefを結合して返す
 */
export function mergeRefs<T>(
  refs: Array<MutableRefObject<T> | LegacyRef<T>>
): RefCallback<T> {
  return (value) => {
    refs.forEach((ref) => {
      if (typeof ref === 'function') {
        ref(value)
      } else if (ref != null) {
        (ref as MutableRefObject<T | null>).current = value
      }
    })
  }
}

interface IFormValues {
  LambdaUrl: string;
  RedmineUrl: string;
  RedmineApiKey: string;
  ChangeParameter: string;
  CsvFile: FileList|null;
  AwsAccessKeyId: string;
  AwsSecretId: string;
}

type InputProps = {
  label: Path<IFormValues>;
  register: UseFormRegister<IFormValues>;
  required: boolean;
};
// The following component is an example of your existing Input Component
const Input = ({ label, register, required }: InputProps) => (
  <>
    <TextField label={label} variant="outlined" {...register(label, { required })} fullWidth />
  </>
);

const ChangeParameterKeys = [
  //'#',
  'プロジェクト',
  'トラッカー',
  '親チケット',
  'ステータス',
  '優先度',
  '題名',
  //'作成者',
  '担当者',
  //'更新日',
  'カテゴリ',
  '対象バージョン',
  '開始日',
  '期日',
  '予定工数',
  '合計予定工数',
  '作業時間',
  '合計作業時間',
  '進捗率',
  '関連するチケット',
  //'作成日',
  //'終了日'
]

const App = () => {
  const { register, handleSubmit, reset } = useForm<IFormValues>();
  const [uploading, setUploading] = useState<boolean>(false)
  const [serverResponse, setServerResponse] = useState<Response|null>(null);
  const [responseJson, setResponseJson] = useState([]);

  const onSubmit: SubmitHandler<IFormValues> = async data => {
    if(!data.CsvFile) return;
    const url = data.LambdaUrl;
    setUploading(true);

    const signer = new SignatureV4({
      region: 'ap-northeast-1',
      service: 'lambda',
      sha256: Sha256,
      credentials: {
        accessKeyId: data.AwsAccessKeyId,
        secretAccessKey: data.AwsSecretId,
      },
    });

    let form = new FormData();
    form.set('parameter', data.ChangeParameter);
    form.set('url', data.RedmineUrl);
    form.set('api_key', data.RedmineApiKey);
    form.set('csv_file', await data.CsvFile[0].text());

    const formBody = Object.fromEntries(form.entries());
    const req = await signer.sign(
      new HttpRequest({
        method: 'POST',
        protocol: 'https:',
        path: '/',
        hostname: url,
        headers: {
          host: url,
        },
        body: JSON.stringify(formBody),
      })
    );

    const resp = await fetch(`${req.protocol}//${req.hostname}${req.path}`, {
      method: req.method,
      body: req.body,
      headers: req.headers,
    });

    setUploading(false);
    setServerResponse(resp);
    const responseJson = await resp.json();
    console.log(responseJson);
    setResponseJson(responseJson);
    reset({CsvFile: null, })
  };
  return (
    <Container>
      <form onSubmit={handleSubmit(onSubmit)}>
        <Grid container spacing={2}>
          <Grid item xs={12}>
          </Grid>
          <Grid item xs={12}>
            <Input label="AwsAccessKeyId" register={register} required />
          </Grid>
          <Grid item xs={12}>
            <Input label="AwsSecretId" register={register} required />
          </Grid>
          <Grid item xs={12}>
            <Input label="LambdaUrl" register={register} required />
          </Grid>
          <Grid item xs={12}>
            <Input label="RedmineUrl" register={register} required />
          </Grid>
          <Grid item xs={12}>
            <Input label="RedmineApiKey" register={register} required />
          </Grid>
          <Grid item xs={6}>
            <FormControl fullWidth>
              <InputLabel id="change-parameter-label-id">ChangeParameter</InputLabel>
              <Select
                labelId="change-parameter-label-id"
                label="ChangeParameter"
                fullWidth
                {...register('ChangeParameter', { required: true })}
              >
                {
                  ChangeParameterKeys.map(x => <MenuItem value={x}>{x}</MenuItem>)
                }
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12}>
            <InputFile {...register("CsvFile", {required: true})}></InputFile>
          </Grid>
          <Grid item xs={12}>
            <Button variant="contained" type="submit">送信</Button> 
          </Grid>
          <Grid item xs={12}  >
            {uploading && <img src='./loading.gif' alt='loading'/>}
          </Grid>
          <Grid item xs={12}>
            {
              !serverResponse ? <></> : 
                (
                  serverResponse.status === 200 ?
                    <Alert severity="success">Upload Success</Alert> : 
                    <Alert severity="error">エラーが発生。Lambdaで謎のエラーが出たので作った人に聞いてください</Alert>
                )
            }
          </Grid>
          <Grid item xs={12}>
            {
              !serverResponse ? <></> : 
              (
                <List>
                  {
                    responseJson.map( (x, i)=> {
                      return (
                          <Alert key={i} severity={ ([200,201, 204].indexOf(x['issue']['status_code'])!==-1&&[200,201,204].indexOf(x['relation']['status_code'])!==-1)?'success':'error'}>Issue: {x['issue']['id']+': '+x['issue']['text']} / Relation: {x['relation']['text']}</Alert>
                        )
                    })
                  }
                </List>
              )
            }
          </Grid>
        </Grid>
      </form>
    </Container>
  );
};

export default App;
